import { UseFormReturn, useFormState } from 'react-hook-form';
import { DataList, ToggleField } from '@brighte/form-components';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { ApplicantType, FinancePurchaseType, financeSchema } from '@brighte/validation';
import StepFooter from '@components/StepFooter/StepFooter';
import { StepNavigator } from '@components/StepNavigator';
import { useQuoteByTrackingId } from '@hooks';
import { useApplicationData, useStepperActions } from '@provider';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useDisclosure } from '@spark-web/utils';
import {
  APPLICATION_STEPS,
  FINANCE_PURCHASE,
  LABELS,
  getFormFields,
  isFieldExists,
  isJointApplicant,
} from '../../utils';
import { FinancePurchaseFields } from './FinancePurchaseFields';
import { PaymentSelection } from './PaymentSelection';
import { useFinanceSummary } from './hooks';
import { financialProductLabel, getDisclaimers } from './utils';
import { getDescriptionByFinancialProduct } from './utils/helpers';

function FinancePurchase() {
  const { TITLE, MULTI_PRODUCT_DIALOG_TITLE, MULTI_PRODUCT_DIALOG_CONTENT } = FINANCE_PURCHASE;
  const { user, verificationCompleted } = useAuth0();

  const { isLoading } = useQuoteByTrackingId({ enabled: !!user && verificationCompleted });
  const { quote, application, Finance: finance } = useApplicationData(['quote', 'application', 'Finance']);
  const isJoint = isJointApplicant();
  const financePurchaseSummary = useFinanceSummary();
  const isCashEnabled = quote?.productConfiguration?.cashPaymentEnabled ?? false;
  const { isOpen, onToggle } = useDisclosure();
  const { onNext } = useStepperActions();

  const Footer = ({ watch, control }: Pick<UseFormReturn<FinancePurchaseType>, 'watch' | 'control'>) => {
    const isCashApplication = watch('payment.isCashApplication');
    const terms = watch('repaymentTerm');
    const footerItems = getDisclaimers({
      quote,
      applicationSubStatus: application?.subStatus,
      selectedTerm: terms,
      isCashApplication,
    });

    const form = useFormState({ control });

    // this is due to brighte-flex which term is preselected
    const selectedTerm = typeof terms === 'number';

    // show footer  repayment is selected or if cash application is selected
    const showFooter =
      (isCashEnabled && form.dirtyFields['payment.isCashApplication']) ||
      form.dirtyFields['repaymentTerm'] ||
      selectedTerm;
    return footerItems.length && showFooter ? <StepFooter items={footerItems} /> : null;
  };

  const additionalFields = getFormFields(financeSchema, quote?.financialProduct.id ?? '');
  const hasOwnProperty = isFieldExists(additionalFields, 'applicant.ownProperty');

  return (
    <StepNavigator<FinancePurchaseType>
      schema={financeSchema}
      name={APPLICATION_STEPS.FINANCE_PURCHASE}
      title={TITLE}
      isLoading={isLoading}
      defaultValues={finance || {}}
      completedContent={financePurchaseSummary}
      continueLabel={LABELS.CONTINUE}
      footerContent={({ watch, control }: UseFormReturn<FinancePurchaseType>) => (
        <Footer watch={watch} control={control} />
      )}
      onBeforeNextStep={(formValues: FinancePurchaseType) => {
        const hasMultipleProducts =
          (quote?.relatedReferrals?.length ?? 0) > 0 &&
          (quote?.productConfiguration?.supportMultipleCategories ?? false);
        const isCashApplication = formValues?.payment?.isCashApplication;

        // If there is single product or it's a cash application, don't show the dialog and continue application
        if (!hasMultipleProducts || isCashApplication) {
          return false;
        }

        onToggle();
        return !isOpen;
      }}
    >
      {({ control, setValue, watch, resetField, register }: UseFormReturn<FinancePurchaseType>) => {
        const isCashApplication = watch('payment.isCashApplication');
        return (
          <>
            {isJoint && hasOwnProperty ? (
              // Needs an explicit gap: this Stack builds its own layout rather than relying on
              // StepNavigator's, and DataList no longer carries internal padding of its own.
              <Stack gap="xlarge">
                {financePurchaseSummary?.map(({ title, items }, index) => (
                  <DataList title={title} items={items} key={`${title}-${index}`} />
                ))}
                <ToggleField
                  name="applicant.ownProperty"
                  label={LABELS.I_OWN_PROPERTY}
                  control={control}
                  layout="inline"
                />
              </Stack>
            ) : (
              <>
                <Text>{getDescriptionByFinancialProduct(quote?.financialProduct)}</Text>
                <PaymentSelection
                  control={control}
                  setValue={setValue}
                  resetField={resetField}
                  financialProduct={quote?.financialProduct}
                  isCashPaymentEnabled={quote?.productConfiguration?.cashPaymentEnabled}
                />
                {isCashApplication !== undefined ? (
                  <FinancePurchaseFields
                    watch={watch}
                    control={control}
                    setValue={setValue}
                    register={register}
                    isCashApplication={isCashApplication}
                  />
                ) : null}
              </>
            )}
            <ContentDialog title={MULTI_PRODUCT_DIALOG_TITLE} isOpen={isOpen} onToggle={onToggle}>
              <Text>{MULTI_PRODUCT_DIALOG_CONTENT(financialProductLabel(quote?.financialProduct) ?? '')}</Text>
              <Columns gap="xxlarge">
                <Button
                  onClick={() => {
                    onToggle();
                    onNext();
                  }}
                >
                  Yes, proceed
                </Button>
                <Button prominence="low" onClick={onToggle}>
                  Cancel
                </Button>
              </Columns>
            </ContentDialog>
            <input
              type="hidden"
              {...register('applicantType')}
              value={isJoint ? ApplicantType.Joint : ApplicantType.Primary}
            />
            <input type="hidden" {...register('financialProductId')} value={quote?.financialProduct.id} />
          </>
        );
      }}
    </StepNavigator>
  );
}

export default FinancePurchase;
